package org.multiplatform.kotfolio.pages.compose.viewmodel

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/viewmodel/autocloseable")
@Composable
fun ViewModelAutoCloseablePage() {
    BlogLayout(
        PageProperties(
            "ViewModel AutoCloseable",
            ""
        ),
        BlogProperties("ViewModel", "AutoCloseable")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It’s the traditional resting place for teardown logic-closing database handles, cancelling network subscriptions, or unregistering event listeners.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By combining Java/Kotlin’s AutoCloseable interface with ViewModel.addCloseable(), you can write self-cleaning dependencies that automatically tear themselves down when the ViewModel dies — without writing a single line of code in onCleared().",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Core Concept",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "AutoCloseable: A standard interface with a single close() method representing a resource that needs to be released when no longer needed.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel.addCloseable(AutoCloseable): An API in androidx.lifecycle that accepts any AutoCloseable instance. When the ViewModel is cleared, Android automatically iterates over every registered closeable and invokes its close() method.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    import androidx.lifecycle.ViewModel
                    import dagger.hilt.android.lifecycle.HiltViewModel
                    import javax.inject.Inject

                    @HiltViewModel
                    class CryptoTickerViewModel @Inject constructor(
                        private val webSocketManager: LiveTickerWebSocketManager
                    ) : ViewModel() {
                        init {
                            //The magic line: Register the closeable resource
                            addCloseable(webSocketManager)

                            //Start listening to the live stream
                            webSocketManager.connect(
                                url = "",
                                listener = TickerWebSocketListener()
                            )
                        }

                        //Look, Ma! No onCleared() override required!
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "What is the purpose of AutoCloseable in the viewModel constructor?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This parameter is not a dependency injection feature, but rather a resource ownship mechanism.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The close() function of the AutoCloseable resource you pass to the ViewModel is automatically executed when the ViewModel is permanently cleaned up and before onCleared() is called.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //1
                    class MyViewModel(
                        private val workerScope: CloseableCoroutineScope
                    ) : ViewModel(workerScope) {
                        // Framework automatically calls workerScope.close() when cleared
                    }
                    
                    //2
                    class NewsSubscription(
                        private val connection: NewsConnection
                    ) : AutoCloseable {
                        override fun close()
                        {
                           connection.close()
                        }
                    }
                    
                    class NewsViewModel(
                        private val subscription: NewsSubscription
                    ) : ViewModel(subscription)
                    {
                        
                    }
                    """.trimIndent()
            )
        }
    }
}